/* =============================================================================
 * M.M.LaFleur — header search dropdown.
 *
 * Queries Shopify's predictive search endpoint and renders product hits in the
 * panel. Notes on the approach:
 *
 *   1. The opening click is captured, not bubbled. The theme runs Barba page
 *      transitions, which binds a bubble-phase click listener on document to
 *      intercept links — so a bubble-phase preventDefault here would come too
 *      late and the search page would load underneath the panel. Same lesson as
 *      the cart drawer.
 *
 *   2. Requests are debounced and the previous one is aborted. Typing "blazer"
 *      would otherwise fire six queries whose responses can land out of order,
 *      leaving results for "blaz" on screen.
 *
 *   3. The results grid is not a live region. It is rebuilt on every settled
 *      keystroke, and announcing it wholesale meant a screen reader re-read
 *      every card each time. A separate visually-hidden status line carries a
 *      short summary ("6 results") instead, on its own, slower debounce, so
 *      only the query you stopped on is spoken.
 *
 *   4. Titles are cleaned the way the rest of the theme cleans them: colourways
 *      are separate products named "Name - Fabric :: Colour", so the fabric
 *      becomes the eyebrow and the colour is dropped.
 * ========================================================================== */

(function () {
  "use strict";

  var MIN_CHARS = 2;
  var DEBOUNCE_MS = 220;
  /* Longer than DEBOUNCE_MS plus a typical round trip, so a burst of typing
     produces one announcement for the final query, not one per response. */
  var ANNOUNCE_MS = 700;
  var LIMIT = 6;

  var root = null;
  var input = null;
  var results = null;
  var panel = null;
  var status = null;
  var announceTimer = null;
  var lastFocus = null;
  var timer = null;
  var controller = null;

  function init() {
    root = document.querySelector("[data-mm-search]");
    if (!root) return false;
    input = root.querySelector("[data-mm-search-input]");
    results = root.querySelector("[data-mm-search-results]");
    panel = root.querySelector(".mm-search-panel");
    status = root.querySelector("[data-mm-search-status]");
    return true;
  }

  /* --- helpers ---------------------------------------------------------- */

  function esc(str) {
    return String(str == null ? "" : str)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }

  /* "Moreland Travel Blazer - OrigamiTech :: Black" -> name + fabric */
  function parseTitle(title) {
    var base = String(title || "").split(" :: ")[0];
    var bits = base.split(" - ");
    return {
      name: (bits[0] || base).trim(),
      fabric: bits.length > 1 ? bits[bits.length - 1].trim() : ""
    };
  }

  var formatter = null;
  function money(value) {
    if (value == null || value === "") return "";
    var amount = parseFloat(String(value).replace(/[^0-9.]/g, ""));
    if (isNaN(amount)) return "";
    if (formatter === null) {
      var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;
      try {
        formatter = code
          ? new Intl.NumberFormat(undefined, {
              style: "currency", currency: code,
              minimumFractionDigits: 0, maximumFractionDigits: 0
            })
          : false;
      } catch (err) {
        formatter = false;
      }
    }
    if (formatter) return formatter.format(amount);
    return "$" + Math.round(amount);
  }

  /* Predictive search has shipped the product image under several different
     keys across API versions — a bare `image` string on some, a `featured_image`
     object on others, and nothing at the top level on a few, where only the
     first variant carries one. Check each rather than assume a shape. */
  function pickImage(p) {
    if (!p) return "";
    if (typeof p.image === "string" && p.image) return p.image;
    if (p.image && p.image.url) return p.image.url;
    if (typeof p.featured_image === "string" && p.featured_image) return p.featured_image;
    if (p.featured_image && p.featured_image.url) return p.featured_image.url;
    if (p.variants && p.variants.length) {
      var v = p.variants[0];
      if (typeof v.image === "string" && v.image) return v.image;
      if (v.image && v.image.url) return v.image.url;
      if (v.featured_image && v.featured_image.url) return v.featured_image.url;
    }
    return "";
  }

  /* Predictive search returns a small image; ask the CDN for a crisper one. */
  function thumb(url) {
    if (!url) return "";
    try {
      var u = new URL(url, window.location.href);
      u.searchParams.set("width", "400");
      return u.href;
    } catch (err) {
      return url;
    }
  }

  /* --- rendering -------------------------------------------------------- */

  /* Clearing first and writing on the next tick makes a repeat of the same
     message ("No results" twice in a row) register as a change, so it is
     spoken again rather than silently swallowed. */
  function announce(message) {
    window.clearTimeout(announceTimer);
    if (!status) return;
    if (!message) { status.textContent = ""; return; }
    announceTimer = window.setTimeout(function () {
      status.textContent = "";
      window.setTimeout(function () { status.textContent = message; }, 50);
    }, ANNOUNCE_MS);
  }

  function note(html) {
    results.innerHTML = '<p class="mm-search-note">' + html + "</p>";
  }

  function render(products, query) {
    if (!products.length) {
      note("No matches for <strong>" + esc(query) + "</strong>. Try a style name, a fabric, or a fit.");
      announce("No results");
      return;
    }

    var cards = products.slice(0, LIMIT).map(function (p) {
      var parsed = parseTitle(p.title);
      var img = thumb(pickImage(p));
      var price = money(p.price);
      return '<a class="mm-search-hit" href="' + esc(p.url) + '">' +
        '<span class="mm-search-hit-media">' +
          (img ? '<img src="' + esc(img) + '" alt="" loading="lazy" decoding="async">' : "") +
        "</span>" +
        (parsed.fabric ? '<p class="mm-eyebrow mm-muted">' + esc(parsed.fabric) + "</p>" : "") +
        "<h4>" + esc(parsed.name) + "</h4>" +
        (price ? "<p>" + esc(price) + "</p>" : "") +
        "</a>";
    }).join("");

    var all = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root : "/") + "search?type=product&q=" + encodeURIComponent(query);

    results.innerHTML =
      '<div class="mm-search-grid">' + cards + "</div>" +
      '<div class="mm-search-foot">' +
        '<a class="mm-search-all mm-underline mm-underline-hover" href="' + esc(all) + '">' +
          "See all results for &ldquo;" + esc(query) + "&rdquo;" +
        "</a>" +
      "</div>";

    var shown = Math.min(products.length, LIMIT);
    announce(shown === 1 ? "1 result" : shown + " results");
  }

  function search(query) {
    if (controller) controller.abort();
    controller = ("AbortController" in window) ? new AbortController() : null;

    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root : "/");
    var url = base + "search/suggest.json?q=" + encodeURIComponent(query) +
      "&resources[type]=product&resources[limit]=" + LIMIT +
      "&resources[options][unavailable_products]=last";

    fetch(url, {
      headers: { Accept: "application/json" },
      signal: controller ? controller.signal : undefined
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        render(products, query);
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
        note("Search is unavailable right now. Press Enter to run a full search.");
        announce("Search is unavailable right now. Press Enter to run a full search.");
      });
  }

  /* --- open / close ----------------------------------------------------- */

  /* On the homepage the header floats transparent over the hero until you
     scroll. With the search panel open that reads badly: a bone panel hanging
     off a header you can see straight through, sitting on top of the artwork.

     `data-floating` is written server-side by mm_header.liquid and drives every
     floating rule at once — the transparent background, the cream link colour,
     the inverted wordmark, the text shadow. Flipping that single attribute
     puts the header into its solid state without touching `data-scrolled`,
     which the scroll handler in mm-editorial.js owns. The previous value is
     kept so pages where the header was already solid are restored correctly. */
  var headerRoot = null;
  var floatingWas = null;

  function holdHeaderSolid() {
    headerRoot = document.querySelector(".mm-header-root");
    if (!headerRoot) return;
    floatingWas = headerRoot.getAttribute("data-floating");
    headerRoot.setAttribute("data-floating", "false");
  }

  function releaseHeader() {
    if (!headerRoot) return;
    if (floatingWas === null) headerRoot.removeAttribute("data-floating");
    else headerRoot.setAttribute("data-floating", floatingWas);
    headerRoot = null;
    floatingWas = null;
  }

  function open() {
    if (!root && !init()) return;
    if (root.getAttribute("data-open") === "true") return;
    lastFocus = document.activeElement;
    holdHeaderSolid();
    root.hidden = false;
    window.requestAnimationFrame(function () {
      root.setAttribute("data-open", "true");
      if (input) input.focus();
    });
    document.body.classList.add("mm-no-scroll");
  }

  function close() {
    if (!root || root.hidden) return;
    root.setAttribute("data-open", "false");
    document.body.classList.remove("mm-no-scroll");
    releaseHeader();
    window.setTimeout(function () {
      if (root.getAttribute("data-open") === "false") {
        root.hidden = true;
        if (results) results.innerHTML = "";
        if (input) input.value = "";
        announce("");
      }
    }, 260);
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
    lastFocus = null;
  }

  function isOpen() {
    return root && !root.hidden && root.getAttribute("data-open") === "true";
  }

  /* --- wiring ----------------------------------------------------------- */

  document.addEventListener("click", function (e) {
    if (!root && !init()) return;
    if (!e.target || !e.target.closest) return;

    if (e.target.closest("[data-mm-search-close]")) {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }

    var trigger = e.target.closest('a[href$="/search"], a[href*="/search?"]');
    /* Ignore anything inside the panel itself, so "See all results" still
       navigates to the full page. */
    if (trigger && root.contains(trigger)) return;

    if (trigger && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
      e.preventDefault();
      e.stopPropagation();
      open();
    }
  }, true);

  document.addEventListener("input", function (e) {
    if (!input || e.target !== input) return;
    var query = input.value.trim();
    window.clearTimeout(timer);

    if (query.length < MIN_CHARS) {
      if (controller) controller.abort();
      results.innerHTML = "";
      announce("");
      return;
    }
    timer = window.setTimeout(function () { search(query); }, DEBOUNCE_MS);
  });

  /* Focus trap — the cart drawer's contract (mm-cart.js): Tab and Shift+Tab
     wrap inside the panel rather than walking out into the page behind it.
     Visibility is tested with getClientRects rather than offsetParent, which
     is null for anything position: fixed and would silently empty the list.
     idx is -1 when focus is on the panel itself or has left it; that wraps
     too, so one Tab always brings focus back in. */
  function stops() {
    if (!panel) return [];
    return Array.prototype.filter.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (n) { return n.getClientRects().length > 0; }
    );
  }

  document.addEventListener("keydown", function (e) {
    if (!isOpen()) return;
    if (e.key === "Escape") { e.preventDefault(); close(); return; }
    if (e.key !== "Tab") return;

    var list = stops();
    if (!list.length) { e.preventDefault(); return; }
    var idx = list.indexOf(document.activeElement);
    if (e.shiftKey && idx <= 0) { e.preventDefault(); list[list.length - 1].focus(); }
    else if (!e.shiftKey && (idx === -1 || idx === list.length - 1)) { e.preventDefault(); list[0].focus(); }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
}());
